import type { JSX } from 'react';
import { useState } from 'react';
import { Badge, Button, Group, SegmentedControl, Stack, Table, Text, Tooltip } from '@mantine/core';
import { tenderApi, type PackageDetail } from '../../api';
import { formatAmount, formatEuro, formatPercent, formatQuantity } from '../../format';
import { depths } from './outline';

/**
 * The price comparison ("Preisspiegel"): positions down, bidders across, the
 * lowest price of each row marked, and the totals, discounts and ranking at
 * the foot - above it the offer totals as bars against the estimate.
 */
export function ComparisonPanel({ projectId, detail }: { projectId: string; detail: PackageDetail }): JSX.Element {
  const [mode, setMode] = useState<'gp' | 'ep'>('gp');
  const ev = detail.evaluation;
  const cmp = new Map(ev.items.map((c) => [c.itemId, c]));
  const results = new Map(ev.bidders.map((r) => [r.bidderId, r]));
  const ranked = [...ev.bidders].filter((r) => r.rank !== null).sort((a, b) => a.rank! - b.rank!);
  const bidders = [
    ...ranked.map((r) => detail.bidders.find((b) => b.id === r.bidderId)!),
    ...detail.bidders.filter((b) => results.get(b.id)!.rank === null),
  ];
  // Subtitles and their positions indented, subtotals on every level (AP-113).
  const depth = depths(detail.items);
  const indent = (id: string): string => `calc(${depth.get(id) ?? 0} * var(--mantine-spacing-md))`;

  if (detail.bidders.length === 0 || ranked.length === 0) {
    return <Text size="sm" c="dimmed" ta="center" py="lg">Der Preisspiegel entsteht, sobald für mindestens ein Angebot Preise vorliegen.</Text>;
  }

  return (
    <Stack gap="md">
      <TotalsChart detail={detail} />
      <Group justify="space-between">
        <SegmentedControl size="xs" value={mode} onChange={(v) => setMode(v as 'gp' | 'ep')} data={[{ value: 'gp', label: 'Gesamtpreise' }, { value: 'ep', label: 'Einheitspreise' }]} />
        <Button size="xs" variant="default" component="a" href={tenderApi.comparisonCsvUrl(projectId, detail.package.id)}>Preisspiegel als CSV</Button>
      </Group>
      <Table.ScrollContainer minWidth={420 + bidders.length * 150}>
        <Table verticalSpacing={3} className="pst-tabular" withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={90}>OZ</Table.Th>
              <Table.Th>Kurztext</Table.Th>
              <Table.Th w={100} ta="right">Menge</Table.Th>
              <Table.Th w={120} ta="right">Schätzung (€)</Table.Th>
              {bidders.map((b) => {
                const r = results.get(b.id)!;
                return (
                  <Table.Th key={b.id} w={150} ta="right">
                    <Tooltip label={b.company}><Text size="xs" fw={600} lineClamp={2}>{b.company}</Text></Tooltip>
                    {r.rank ? <Badge size="xs" variant={r.rank === 1 ? 'filled' : 'light'} color={r.rank === 1 ? 'green' : 'gray'}>Rang {r.rank}</Badge> : <Badge size="xs" variant="outline" color="gray">ohne Rang</Badge>}
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {detail.items.map((item) => {
              // Notes (Vorbemerkungen) carry no prices.
              if (item.kind === 'note') return null;
              if (item.kind === 'title') {
                return (
                  <Table.Tr key={item.id} style={{ background: 'var(--mantine-color-default-hover)' }}>
                    <Table.Td><Text size="sm" ff="monospace" fw={600}>{item.oz}</Text></Table.Td>
                    <Table.Td colSpan={3} pl={indent(item.id)}><Text size="sm" fw={600}>{item.shortText}</Text></Table.Td>
                    {bidders.map((b) => (
                      <Table.Td key={b.id} ta="right"><Text size="sm" fw={600}>{mode === 'gp' ? formatAmount(ev.titleTotals[item.id]?.[b.id] ?? null) : ''}</Text></Table.Td>
                    ))}
                  </Table.Tr>
                );
              }
              const c = cmp.get(item.id)!;
              const est = mode === 'gp' ? c.estimateTotal : item.estimateUnitPrice;
              return (
                <Table.Tr key={item.id} style={c.countsForTotal ? undefined : { opacity: 0.7 }}>
                  <Table.Td><Text size="sm" ff="monospace" c="dimmed">{item.oz}</Text></Table.Td>
                  <Table.Td pl={indent(item.id)}>
                    <Text size="sm" lineClamp={1} title={item.shortText}>{item.shortText}</Text>
                    {!c.countsForTotal && <Text size="xs" c="dimmed">nicht in der Summe</Text>}
                  </Table.Td>
                  <Table.Td ta="right"><Text size="sm">{formatQuantity(item.quantity)} {item.unit}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" c="dimmed">{formatAmount(est)}</Text></Table.Td>
                  {bidders.map((b) => {
                    const up = c.unitPrices[b.id] ?? null;
                    const value = mode === 'gp' ? (c.totals[b.id] ?? null) : up;
                    const lowest = up !== null && up === c.lowestUnitPrice && c.lowestUnitPrice !== c.highestUnitPrice && results.get(b.id)!.rank !== null;
                    const highest = up !== null && up === c.highestUnitPrice && c.lowestUnitPrice !== c.highestUnitPrice && results.get(b.id)!.rank !== null;
                    const vsMean = up !== null && c.meanUnitPrice ? ((up - c.meanUnitPrice) / c.meanUnitPrice) * 100 : null;
                    return (
                      <Table.Td
                        key={b.id}
                        ta="right"
                        style={lowest ? { background: 'var(--mantine-color-green-light)' } : undefined}
                        title={up === null ? 'kein Preis' : `EP ${formatAmount(up)} € · ${formatPercent(vsMean, true)} zum Mittel${lowest ? ' · niedrigster' : highest ? ' · höchster' : ''}`}
                      >
                        <Text size="sm" c={up === null ? 'orange.7' : undefined} fw={lowest ? 600 : undefined}>
                          {up === null ? 'fehlt' : formatAmount(value)}
                          {lowest && <Text span size="xs" c="green.8"> ▼</Text>}
                          {highest && <Text span size="xs" c="dimmed"> ▲</Text>}
                        </Text>
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
          <Table.Tfoot>
            <FootRow label="Summe netto" bidders={bidders} value={(id) => formatAmount(results.get(id)!.net)} estimate={formatAmount(ev.estimateTotal)} />
            <FootRow label="Nachlass" bidders={bidders} value={(id) => { const r = results.get(id)!; const b = detail.bidders.find((x) => x.id === id)!; return r.discount ? `−${formatAmount(r.discount)} (${formatPercent(b.discountPercent)})` : '—'; }} />
            <FootRow label="Netto nach Nachlass" strong bidders={bidders} value={(id) => formatAmount(results.get(id)!.netAfterDiscount)} estimate={formatAmount(ev.estimateTotal)} />
            <FootRow label={`Umsatzsteuer ${detail.package.vatRate} %`} bidders={bidders} value={(id) => formatAmount(results.get(id)!.vat)} />
            <FootRow label="Brutto" strong bidders={bidders} value={(id) => formatAmount(results.get(id)!.gross)} />
            <FootRow label="Skonto (zählt nicht)" bidders={bidders} value={(id) => { const r = results.get(id)!; return r.skonto ? `−${formatAmount(r.skonto)}` : '—'; }} />
            <FootRow label="Abstand zum günstigsten" bidders={bidders} value={(id) => { const r = results.get(id)!; return r.rank === 1 ? 'günstigstes' : formatPercent(r.aboveLowestPercent, true); }} />
            <FootRow label="Gegenüber Schätzung" bidders={bidders} value={(id) => formatPercent(results.get(id)!.againstEstimatePercent, true)} />
            <FootRow label="Fehlende Preise" bidders={bidders} value={(id) => { const m = results.get(id)!.missing; return m ? String(m) : '—'; }} />
          </Table.Tfoot>
        </Table>
      </Table.ScrollContainer>
      <Text size="xs" c="dimmed">▼ niedrigster, ▲ höchster Einheitspreis der Zeile unter den gewerteten Angeboten. Beim Überfahren: Abweichung zum Mittelwert.</Text>
    </Stack>
  );
}

function FootRow({ label, bidders, value, estimate, strong }: { label: string; bidders: { id: string }[]; value: (id: string) => string; estimate?: string; strong?: boolean }): JSX.Element {
  return (
    <Table.Tr>
      <Table.Td colSpan={3}><Text size="sm" fw={strong ? 700 : 500}>{label}</Text></Table.Td>
      <Table.Td ta="right"><Text size="sm" c="dimmed">{estimate ?? ''}</Text></Table.Td>
      {bidders.map((b) => (
        <Table.Td key={b.id} ta="right"><Text size="sm" fw={strong ? 700 : undefined}>{value(b.id)}</Text></Table.Td>
      ))}
    </Table.Tr>
  );
}

/**
 * Offer totals after discount, one bar per ranked bidder, lowest first, with
 * the estimate as a dashed reference line. One series, one colour; the value
 * stands beside each bar, so nothing depends on reading the colour.
 */
function TotalsChart({ detail }: { detail: PackageDetail }): JSX.Element {
  const ev = detail.evaluation;
  const rows = ev.bidders.filter((r) => r.rank !== null).sort((a, b) => a.rank! - b.rank!);
  const max = Math.max(...rows.map((r) => r.netAfterDiscount), ev.estimateTotal ?? 0) * 1.08;
  const pct = (v: number): string => `${(v / max) * 100}%`;
  return (
    <Stack gap={6} aria-label="Angebotssummen im Vergleich" role="img">
      {rows.map((r) => {
        const b = detail.bidders.find((x) => x.id === r.bidderId)!;
        const awarded = detail.package.awardedBidderId === b.id;
        return (
          <Group key={r.bidderId} gap="sm" wrap="nowrap" title={`${b.company}: ${formatEuro(r.netAfterDiscount)} netto nach Nachlass`}>
            <Text size="sm" w={230} truncate="end" fw={r.rank === 1 ? 600 : undefined}>{r.rank}. {b.company}</Text>
            <div style={{ position: 'relative', flex: 1, height: 18 }}>
              <div style={{ position: 'absolute', left: 0, top: 3, height: 12, width: pct(r.netAfterDiscount), borderRadius: '0 4px 4px 0', background: 'var(--mantine-color-brand-6)', opacity: r.rank === 1 ? 1 : 0.55 }} />
              {ev.estimateTotal !== null && (
                <div style={{ position: 'absolute', left: pct(ev.estimateTotal), top: 0, bottom: 0, borderLeft: '2px dashed var(--mantine-color-dimmed)' }} />
              )}
            </div>
            <Text size="sm" w={200} ta="right" className="pst-tabular">
              {r.missing > 0 && <Text span size="xs" c="orange.7">unvollständig · </Text>}
              {formatEuro(r.netAfterDiscount)}
              {awarded && <Badge ml={6} size="xs" color="green">vergeben</Badge>}
            </Text>
          </Group>
        );
      })}
      {ev.estimateTotal !== null && (
        <Text size="xs" c="dimmed">Gestrichelt: Schätzung {formatEuro(ev.estimateTotal)} netto. Balken: Angebot netto nach Nachlass. Unvollständige Angebote stehen hinter den vollständigen.</Text>
      )}
    </Stack>
  );
}
